import api from '@/api/login'
import { Contexto, FormId, Operadora, SSO, Usuario } from '@/store/models/auth'
import { Status } from '@/store/models/status'
import { getField } from 'vuex-map-fields'
import tokenRefresher from '@/services/token-refresher'

// Initial state
const initialState = () => ({
  token: '',
  loading: false,
  contexto: new Contexto(),
  operadora: new Operadora(),
  operadoras: [],
  permissaoRouter: [],
  cards: [],
  sso: new SSO(),
  usuario: new Usuario(),
  idsforms: new FormId()
})

// root state object.
// each Vuex instance is just a single state tree.
const state = initialState

// getters are functions
const getters = {
  getField,
  token: state => state.token,
  contexto: state => state.contexto,
  operadoras: state => state.operadoras,
  operadora: state => state.operadora,
  usuario: state => state.usuario,
  cards: state => state.cards,
  idsforms: state => state.idsforms,
  instanciasAutorizadas: state => ((state.usuario.profile || {}).instancia_aplicacao || {}),
  instanciasAutorizadasGestor: state => ((state.usuario.profile || {}).instancia_aplicacao_gestor || {}),
  isGestorAtendimento: state => ((state.usuario.profile || {}).gestor_atendimento)
}

// actions are functions that cause side effects and can involve
// asynchronous operations.
const actions = {
  initialState ({ commit }) {
    commit('INITIAL_STATE')
  },
  setLoginSSO ({ commit }, payload) {
    commit('SET_LOGIN_SSO', payload)
  },
  resetLoginSSO ({ commit }) {
    commit('RESET_LOGIN_SSO')
  },
  startLoading ({ commit }) {
    commit('LOADING_START')
  },
  stopLoading ({ commit }) {
    commit('LOADING_STOP')
  },
  async validaContexto ({ commit, state }, form) {
    commit('CONTEXTO_PENDING')

    await api.validaContexto(form.contexto).then((res) => {
      if (res.data.status) {
        res.data.contexto = form.contexto
        commit('CONTEXTO_SUCCESS', res.data)
        // Atualizando status da requisição
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'CONTEXTO_SUCCESS',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      } else {
        commit('CONTEXTO_FAILURE', res.data)
        // Atualizando status da requisição
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'CONTEXTO_FAILURE',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      }
    }).catch((error) => {
      commit('CONTEXTO_FAILURE', error)
      // Atualizando status da requisição
      commit('status/UPDATE_REQUEST_STATUS', {
        type: 'CONTEXTO_FAILURE',
        status: false,
        statusCode: (error.response && error.response.status) ? error.response.status : 500,
        message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
      }, { root: true })
    })
  },
  async validaLogin ({ commit, state }, form) {
    commit('LOGIN_PENDING')
    try {
      const res = await api.validaLogin(form)
      if (res.data.status) {
        commit('LOGIN_SUCCESS', res.data)
        // verifica se há somente uma operadora e redireciona com op selecionada
        if (res.data.data.operadoras.length === 1) {
          const payload = {
            operadora: res.data.data.operadoras.pop(),
            token: res.data.data.token
          }
          api.getUsuario(payload.token).then((result) => {
            if (result.data.status) {
              payload.usuario = result.data.data
              // Set Cookie auth
              const period = 86240 // Aproximadamente 24h em segundos
              document.cookie = `${process.env.VUE_APP_COOKIE_NOME}=${payload.token}; Max-age=${period}; domain=${process.env.VUE_APP_COOKIE_DOMAIN}; path=/`
              commit('SELECIONA_OPERADORA_SUCCESS', payload)

              // Verifica entrada de dados SSO para redirecionamento
              if (state.sso && state.sso.token && state.sso.refererUrl) {
                const urlLoginSSO = `${process.env.VUE_APP_API_OMNI_USUARIO}/login/sso`
                const paramsSSO = `token=${state.sso.token}&referer_url=${state.sso.refererUrl}&sandbox=${state.sso.sandbox}`
                // Remove dados sso da Store
                commit('RESET_LOGIN_SSO')
                // Redireciona rota
                window.location.replace(`${urlLoginSSO}?${paramsSSO}`)
              } else {
                // Atualizando status da requisição
                commit('status/UPDATE_REQUEST_STATUS', {
                  type: 'SELECIONA_OPERADORA_SUCCESS',
                  status: res.data.status,
                  statusCode: res.data.statusCode,
                  message: res.data.message
                }, { root: true })
              }
            } else {
              commit('SELECIONA_OPERADORA_FAILURE')
              // Atualizando o status da requisição
              commit('status/UPDATE_REQUEST_STATUS', {
                type: 'SELECIONA_OPERADORA_FAILURE',
                status: res.data.status,
                statusCode: res.data.statusCode,
                message: res.data.message
              }, { root: true })
            }
          }).catch(error => {
            commit('SELECIONA_OPERADORA_FAILURE')
            // Atualizando status da requisição
            commit('status/UPDATE_REQUEST_STATUS', {
              type: 'SELECIONA_OPERADORA_FAILURE',
              status: error.status,
              statusCode: (error.response && error.response.status) ? error.response.status : 500,
              message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
            }, { root: true })
          })
        } else {
          // Atualizando status da requisição
          commit('status/UPDATE_REQUEST_STATUS', {
            type: 'LOGIN_SUCCESS',
            status: res.data.status,
            statusCode: res.data.statusCode,
            message: res.data.message
          }, { root: true })
        }
      } else {
        commit('LOGIN_FAILURE', res.data)
        // Atualizando status da requisição
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'LOGIN_FAILURE',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      }
    } catch (error) {
      commit('LOGIN_FAILURE', error)
      // Atualizando status da requisição
      commit('status/UPDATE_REQUEST_STATUS', {
        type: 'LOGIN_FAILURE',
        status: error.status,
        statusCode: (error.response && error.response.status) ? error.response.status : 500,
        message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
      }, { root: true })
    }
  },

  async selecionaOperadora ({ commit, state }, form) {
    commit('SELECIONA_OPERADORA_PENDING')
    await api.selecionaOperadora(form.idCliente).then((res) => {
      if (res.data.status) {
        const payload = {
          operadora: form,
          token: res.data.data
        }
        api.getUsuario(payload.token).then((result) => {
          if (result.data.status) {
            payload.usuario = result.data.data
            commit('SELECIONA_OPERADORA_SUCCESS', payload)
            // Set Cookie auth
            const period = 86240 // Aproximadamente 24h em segundos
            document.cookie = `${process.env.VUE_APP_COOKIE_NOME}=${payload.token}; Max-age=${period}; domain=${process.env.VUE_APP_COOKIE_DOMAIN}; path=/`
            // Verifica entrada de dados SSO para redirecionamento
            if (state.sso && state.sso.token && state.sso.refererUrl) {
              const urlLoginSSO = `${process.env.VUE_APP_API_OMNI_USUARIO}/login/sso`
              const paramsSSO = `token=${state.sso.token}&referer_url=${state.sso.refererUrl}&sandbox=${state.sso.sandbox}`
              // Remove dados sso da Store
              commit('RESET_LOGIN_SSO')
              // Redireciona rota
              window.location.replace(`${urlLoginSSO}?${paramsSSO}`)
            } else {
              // Caso em que não é SSO - Status True
              // Atualizando status da requisição
              commit('status/UPDATE_REQUEST_STATUS', {
                type: 'SELECIONA_OPERADORA_SUCCESS',
                status: res.data.status,
                statusCode: res.data.statusCode,
                message: res.data.message
              }, { root: true })
            }
          } else {
            commit('SELECIONA_OPERADORA_FAILURE')
            // Atualizando o status da requisição
            commit('status/UPDATE_REQUEST_STATUS', {
              type: 'SELECIONA_OPERADORA_FAILURE',
              status: res.data.status,
              statusCode: res.data.statusCode,
              message: res.data.message
            }, { root: true })
          }
        }).catch((error) => {
          commit('SELECIONA_OPERADORA_FAILURE')
          // Atualizando status da requisição
          commit('status/UPDATE_REQUEST_STATUS', {
            type: 'SELECIONA_OPERADORA_FAILURE',
            status: error.status,
            statusCode: (error.response && error.response.status) ? error.response.status : 500,
            message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
          }, { root: true })
        })
      } else {
        commit('SELECIONA_OPERADORA_FAILURE')
        // Atualizando o status da requisição
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'SELECIONA_OPERADORA_FAILURE',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      }
    }).catch((error) => {
      commit('SELECIONA_OPERADORA_FAILURE')
      commit('status/UPDATE_REQUEST_STATUS', {
        type: 'SELECIONA_OPERADORA_FAILURE',
        status: error.status,
        statusCode: (error.response && error.response.status) ? error.response.status : 500,
        message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
      }, { root: true })
    })
  },
  async esqueciMinhaSenha ({ commit, state }, form) {
    commit('ESQUECI_SENHA_PENDING')

    await api.esqueciMinhaSenha(form).then((res) => {
      if (res.data.status) {
        commit('ESQUECI_SENHA_SUCCESS', res.data)
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'ESQUECI_SENHA_SUCCESS',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      } else {
        commit('ESQUECI_SENHA_FAILURE', res.data)
        // Atualizando o status da requisição
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'ESQUECI_SENHA_FAILURE',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      }
    }).catch((error) => {
      commit('ESQUECI_SENHA_FAILURE', error)
      commit('status/UPDATE_REQUEST_STATUS', {
        type: 'ESQUECI_SENHA_FAILURE',
        status: error.status,
        statusCode: (error.response && error.response.status) ? error.response.status : 500,
        message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
      }, { root: true })
    })
  },
  async recuperaSenha ({ commit, state }, { token, form }) {
    commit('RECUPERA_SENHA_PENDING')
    await api.recuperaSenha(token, form).then((res) => {
      if (res.data.status) {
        commit('RECUPERA_SENHA_SUCCESS', res.data)
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'RECUPERA_SENHA_SUCCESS',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      } else {
        commit('RECUPERA_SENHA_FAILURE', res.data)
        // Atualizando o status da requisição
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'RECUPERA_SENHA_FAILURE',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      }
    }).catch((error) => {
      commit('RECUPERA_SENHA_FAILURE', error)
      commit('status/UPDATE_REQUEST_STATUS', {
        type: 'RECUPERA_SENHA_FAILURE',
        status: error.status,
        statusCode: (error.response && error.response.status) ? error.response.status : 500,
        message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
      }, { root: true })
    })
  },

  async logout ({ commit, state }) {
    commit('LOGOUT_PENDING')
    await api.logout()
    let parts = document.cookie.split(`${process.env.VUE_APP_COOKIE_NOME}=`)
    if (parts.length === 2 && state.token) {
      document.cookie = `${process.env.VUE_APP_COOKIE_NOME}=${state.token}; Max-age=0; domain=${process.env.VUE_APP_COOKIE_DOMAIN}; path=/`
    }
    // Restaurando Store
    await commit('INITIAL_STATE')
    commit('status/UPDATE_REQUEST_STATUS', {
      type: 'LOGOUT_SUCCESS',
      status: 'success',
      statusCode: 200,
      message: 'Logout realizado com sucesso!'
    }, { root: true })
    commit('LOGOUT_SUCCESS')
  },
  async loadUsuario ({ commit, state }, form) {
    commit('LOAD_USUARIO_PENDING')

    await api.getUsuario(state.token).then((res) => {
      if (res.data.status) {
        const payload = {
          operadora: form,
          token: res.data.data
        }
        payload.usuario = res.data.data
        commit('LOAD_USUARIO_SUCCESS', payload)
      }
    })
  },
  async getDashboardCards ({ commit }) {
    await api.getDashboardCards().then(res => {
      if (res.data.status) {
        commit('GET_DASHBOARD_CARDS_SUCCESS', res.data)
      } else {
        commit('GET_DASH_BOARD_CARDS_FAILURE')
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'CONTEXTO_FAILURE',
          status: res.data.status,
          statusCode: res.data.statusCode,
          message: res.data.message
        }, { root: true })
      }
    }).catch(async error => {
      commit('GET_DASHBOARD_CARDS_FAILURE')
      const statusCode = (error.response && error.response.status) ? error.response.status : 500
      if (statusCode === 401) {
        let parts = document.cookie.split(`${process.env.VUE_APP_COOKIE_NOME}=`)
        if (parts.length === 2 && state.token) {
          document.cookie = `${process.env.VUE_APP_COOKIE_NOME}=${state.token}; Max-age=0; domain=${process.env.VUE_APP_COOKIE_DOMAIN}; path=/`
        }
        // Restaurando Store
        await commit('INITIAL_STATE')
        commit('status/UPDATE_REQUEST_STATUS', {
          type: 'LOGOUT_SUCCESS',
          status: 'success',
          statusCode: 200,
          message: 'Logout realizado com sucesso!'
        }, { root: true })
        return
      }
      commit('status/UPDATE_REQUEST_STATUS', {
        type: 'CONTEXTO_FAILURE',
        status: false,
        statusCode: statusCode,
        message: (error.response && error.response.statusText) ? error.response.statusText : 'Falha ao realizar requisição no servidor. Se o problema persistir entre em contato com o suporte.'
      }, { root: true })
    })
  },
  async getIds ({ commit }) {
    await api.getFormId().then(res => {
      if (res.data) {
        commit('GET_FORM_ID_SUCCESS', res.data.data)
      } else {
        commit('GET_FORM_ID_FAILURE')
      }
    }).catch(() => {
      commit('GET_FORM_ID_FAILURE')
    })
  }
}

// mutations are operations that actually mutates the state.
// each mutation handler gets the entire state tree as the
// first argument, followed by additional payload arguments.
// mutations must be synchronous and can be recorded by plugins
// for debugging purposes.
const mutations = {
  INITIAL_STATE (state) {
    const initial = initialState()
    state = Object.keys(initial).forEach(key => {
      state[key] = initial[key]
    })
  },
  SET_LOGIN_SSO (state, payload) {
    state.loading = true
    state.sso = payload
    state.loading = false
  },
  RESET_LOGIN_SSO (state) {
    state.sso = new SSO()
    state.loading = false
  },
  LOADING_START (state) {
    state.loading = true
  },
  LOADING_STOP (state) {
    state.loading = false
  },
  LOGIN_PENDING (state) {
    state.loading = true
  },
  LOGIN_SUCCESS (state, payload) {
    state.token = payload.data.token
    let operadoras = payload.data.operadoras
    let result = operadoras.map(item => {
      let data = new Operadora()
      // Verificando id_cliente
      if (item.hasOwnProperty('id_cliente')) {
        item.idCliente = item.id_cliente
        delete item.id_cliente
      }
      Object.keys(data).forEach(key => {
        data[key] = item[key] || ''
      })
      return data
    })
    state.operadoras = result
    state.permissaoRouter = payload.data.permissaoRouter
    state.loading = false
    tokenRefresher.registerToken()
  },
  LOGIN_FAILURE (state) {
    state.token = ''
    state.loading = false
  },
  CONTEXTO_PENDING (state) {
    state.loading = true
  },
  CONTEXTO_SUCCESS (state, payload) {
    let data = new Contexto()
    Object.keys(data).forEach(key => {
      data[key] = payload.data[key]
    })
    state.contexto = data
    state.token = ''
    state.loading = false
  },
  CONTEXTO_FAILURE (state) {
    state.contexto = new Contexto()
    state.token = ''
    state.loading = false
  },
  SELECIONA_OPERADORA_PENDING (state) {
    // state.loading = true
  },
  SELECIONA_OPERADORA_SUCCESS (state, payload) {
    let data = new Operadora()
    // Verificando id_cliente
    if (payload.operadora.hasOwnProperty('id_cliente')) {
      payload.operadora.idCliente = payload.operadora.id_cliente
      delete payload.operadora.id_cliente
    }
    Object.keys(data).forEach(key => {
      data[key] = payload.operadora[key]
    })
    let user = new Usuario()
    Object.keys(user).forEach(key => {
      user[key] = payload.usuario[key]
    })
    state.usuario = user
    state.operadora = data
    state.token = payload.token
    state.loading = false
  },
  SELECIONA_OPERADORA_FAILURE (state) {
    state.operadora = new Operadora()
    state.token = ''
    state.loading = false
    state.usuario = new Usuario()
  },
  ESQUECI_SENHA_PENDING (state) {
    state.loading = true
  },
  ESQUECI_SENHA_SUCCESS (state, payload) {
    let data = new Status()
    Object.keys(data).forEach(key => {
      data[key] = payload[key]
    })
    state.status = data
    state.loading = false
  },
  ESQUECI_SENHA_FAILURE (state, payload) {
    state.status = new Status()
    state.loading = false
  },
  RECUPERA_SENHA_PENDING (state) {
    state.status = new Status()
    // state.loading = true
  },
  RECUPERA_SENHA_SUCCESS (state, payload) {
    let data = new Status()
    Object.keys(data).forEach(key => {
      data[key] = payload[key]
    })
    state.status = data
    state.loading = false
  },
  RECUPERA_SENHA_FAILURE (state, payload) {
    state.status = new Status()
    // state.loading = true
  },
  LOGOUT_PENDING (state) {
    state.loading = true
  },
  LOGOUT_SUCCESS (state) {
    state.loading = false
    tokenRefresher.unregisterToken()
  },
  USUARIO_SUCCESS (state, payload) {
    let data = new Usuario()
    Object.keys(data).forEach(key => {
      data[key] = payload[key]
    })
    state.usuario = data
  },
  LOAD_USUARIO_PENDING (state) {
    state.loading = true
  },
  LOAD_USUARIO_SUCCESS (state, payload) {
    state.loading = false
    let user = new Usuario()
    Object.keys(user).forEach(key => {
      user[key] = payload.usuario[key]
    })
    state.usuario = user
  },
  GET_DASHBOARD_CARDS_SUCCESS (state, payload) {
    state.cards = payload.data
  },
  GET_DASHBOARD_CARDS_FAILURE (state) {
    state.cards = []
  },
  GET_FORM_ID_SUCCESS (state, payload) {
    const forms = new FormId()
    Object.keys(forms).forEach(key => {
      forms[key] = payload.forms[key]
    })
    state.idsforms = forms
  },
  GET_FORM_ID_FAILURE (state) {
    state.idsforms = new FormId()
  }
}

export default {
  state,
  getters,
  actions,
  mutations,
  namespaced: true
}
